import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useData } from '../hooks/useData';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  // Simple token expiration check
  const isTokenExpired = (token) => {
    if (!token) return true;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      return payload.exp < currentTime;
    } catch (error) {
      // If token cannot be parsed, do not force-refresh here; let API handle 401
      return false;
    }
  };
  const { user, userType, loading, refreshToken } = useAuth();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { backendStatus = { state: 'connecting', message: 'Connecting...' }, retryBackendCheck = () => { } } = useData() || {};

  // Check token expiration and refresh if needed
  useEffect(() => {
    const checkToken = async () => {
      if (user && !loading) {
        const token = localStorage.getItem('auth_token');
        if (token && isTokenExpired(token)) {
          setIsRefreshing(true);
          try {
            await refreshToken();
          } catch (error) {
            console.error('Token refresh failed:', error);
          } finally {
            setIsRefreshing(false);
          }
        }
      }
    };

    checkToken();
  }, [user, loading, refreshToken]);

  useEffect(() => {
    if (backendStatus?.state === 'offline') {
      console.error('Backend unavailable during auth loading:', backendStatus?.error || backendStatus?.message || 'Unknown error');
    }
  }, [backendStatus?.state]);

  // Show loading state while checking authentication
  if (loading || isRefreshing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-yellow-100 flex items-center justify-center">
        {backendStatus?.state === 'offline' ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-red-400 to-red-600 rounded-2xl mx-auto flex items-center justify-center mb-4 animate-pulse">
              <svg className="w-10 h-10" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <text x="50" y="70" fontFamily="Arial, sans-serif" fontSize="48" fontWeight="bold" fill="white" textAnchor="middle">SS</text>
              </svg>
            </div>
            <p className="text-red-700 font-medium">{backendStatus?.message || 'Backend unavailable'}</p>
            {backendStatus?.error && (
              <p className="text-red-600 text-sm mt-1">{backendStatus.error}</p>
            )}
            <div className="mt-4">
              <button
                type="button"
                onClick={retryBackendCheck}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-2xl mx-auto flex items-center justify-center mb-4 animate-pulse">
              <svg className="w-10 h-10" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
                <text x="50" y="70" fontFamily="Arial, sans-serif" fontSize="48" fontWeight="bold" fill="white" textAnchor="middle">SS</text>
              </svg>
            </div>
            <p className="text-gray-700">{backendStatus?.message || 'Loading...'}</p>
          </div>
        )}
      </div>
    );
  }

  // Check if user is authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if admin access is required
  if (adminOnly && userType !== 'admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

export default ProtectedRoute;
